import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import NoteIcon from "@/components/Icon/NoteIcon";
import { useAuthValue } from "@/contexts/contextAuth";
import { setScrollHidden } from "@/libs/utils";
import PaPaySetUpIcon from "../Icon/PaPaySetUpIcon";
import SpecsIcons from "./details/specs-icons/SpecsIcon";
import SpecsAdditionalDetails from "./details/specs-additional-details/SpecsAdditionalDetails";
import PricepointPanelSection from "./PricepointPanelSection";
import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";
import { PostHogFlags } from "@/interfaces/PostHogFlags";
import AdditionalFeatures from "./AdditionalFeatures";
import { AppModalType, useAppModalStore } from "@/stores/app-modal.store";
import { createSelectors } from "@/stores/create-selectors";

type Props = {
  data: any;
  isLoanable?: boolean;
  monthlyPayment?: number;
  onMonthlyPaymentClick?: () => void;
};

const VehicleDetailSection = ({
  data,
  isLoanable = false,
  monthlyPayment = 0,
  onMonthlyPaymentClick = () => {},
}: Props) => {
  const {
    setIsSignup,
    setSource,
    setIsListingAction,
    setIsVehicleHistory,
    setRedirectUrl,
  } = useAuthValue();
  const [isAdditionalInfo, setIsAdditionalInfo] = useState<boolean>(false);
  const appModalStore = createSelectors(useAppModalStore);
  const openModal = appModalStore.use.openModal();

  const [isSellerDisclosures, setIsSellerDisclosures] =
    useState<boolean>(false);
  const descriptionLength = 430;

  const [fullDescription, setFullDescription] = useState<string>("");

  const [showUI, setShowUI] = useState<boolean>(false);

  const openDealNowExplainedModal = () => {
    window.dataLayer.push({
      event: "Off-site Listings DealNow",
      source: "Marketing site",
    });

    openModal(
      {
        slug: data.slug,
      },
      AppModalType.DealNowExplainer
    );
  };
  useEffect(() => {
    const newLineCount = (data.vehicleDescription?.match(/\n/g) || []).length;
    if (
      (data.vehicleDescription &&
        data.vehicleDescription.length > descriptionLength) ||
      newLineCount > 4
    ) {
      let index =
        newLineCount > 4
          ? data.vehicleDescription.split("\n", 4).join("\n").length
          : descriptionLength;

      setShowUI(true);
      setFullDescription(`${data.vehicleDescription.substring(0, index)}...`);
    } else {
      setShowUI(false);
      setFullDescription(data.vehicleDescription);
    }
  }, [data.vehicleDescription]);

  const showAdditionalInfo = () => {
    if (!isAdditionalInfo) {
      setIsAdditionalInfo(true);
    } else {
      setIsAdditionalInfo(false);
    }
  };

  const showSellerDisclosures = () => {
    if (!isSellerDisclosures) {
      setIsSellerDisclosures(true);
    } else {
      setIsSellerDisclosures(false);
    }
  };
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const historyReport = () => {
    const isVehicleHistory = `${data?.slug}?action=vehicleHistory&favId=${data?._id}`;
    setIsVehicleHistory(isVehicleHistory);
    setIsSignup(true, { isVehicleHistory });
    setScrollHidden();
  };

  const isFullyVerified =
    data?.userId?.verification?.vouched &&
    data?.userId?.verification?.email &&
    data?.userId?.verification?.phone &&
    data?.userId?.verification?.bank;

  const listingName = `${data.RegistrationYear} ${data.CarMake} ${
    data.CarModel
  } ${data.Trim ? data.Trim : ""}`;

  return (
    <div className="w-full px-4 md:px-0">
      <section className="w-full mt-[24px] md:mt-[30px]">
        <div className="block md:flex md:justify-between md:items-start">
          <div className="listing-name">
            <h1 className="text-[#212529] text-[24px] md:text-[35px] leading-[32px] font-bold">
              {listingName}
            </h1>
            <div className="flex items-center md:mt-2">
              <h2 className="text-[#212529] text-[20px] md:text-[30px] leading-[48px] font-semibold">
                ${data?.Price ? data.Price.toLocaleString() : "--"}
              </h2>
              {monthlyPayment && isLoanable ? (
                <span
                  className="text-[12px] md:text-[16px] ml-3 font-normal leading-[48px] underline cursor-pointer"
                  onClick={onMonthlyPaymentClick}
                >
                  Est. ${monthlyPayment.toFixed(0)}/mo
                </span>
              ) : (
                ""
              )}
            </div>

            {data.listingLocation ? (
              <div className="text-[12px] md:text-[16px] text-[#808080] font-medium">
                {data.listingLocation.city},&nbsp;
                {data.listingLocation.stateShortname}
              </div>
            ) : (
              <span>N/A</span>
            )}
          </div>

          {data?.status !== "sold" && !data?.originalSourceInfo && (
            <div className="listing-cta-container mt-3 md:mt-0 md:flex md:justify-end md:min-w-[300px]">
              <button
                className="btn-outline-primary w-auto md:w-[120px]"
                onClick={() => {
                  setSource("Chat seller");
                  setIsSignup(true, {
                    isListingAction: `${data.slug}?action=message`,
                  });
                  setScrollHidden();
                  setIsListingAction(`${data.slug}?action=message`);
                }}
              >
                Chat seller
              </button>
              <button
                className="btn-outline-primary w-auto md:w-[120px] ml-3"
                onClick={() => {
                  setSource("Schedule Test Drive");
                  setIsSignup(true, {
                    isListingAction: `${data.slug}?action=scheduleTestDrive`,
                  });
                  setScrollHidden();
                  setIsListingAction(`${data.slug}?action=scheduleTestDrive`);
                }}
              >
                Test drive
              </button>
              <button
                className="btn-primary w-auto md:w-[120px] ml-3 max-[350px]:mt-3 max-[350px]:ml-0 max-[350px]:px-6 "
                onClick={() => {
                  setSource("Make Offer");
                  setIsSignup(true, {
                    isListingAction: `${data.slug}?action=createOffer`,
                  });
                  setScrollHidden();
                  setIsListingAction(`${data.slug}?action=createOffer`);
                }}
              >
                Make offer
              </button>
            </div>
          )}

          {data?.status != "sold" && data?.originalSourceInfo && (
            <div className="flex min-w-[300px] mt-3">
              <button
                className="btn-primary bg-[#F7F9FC] border-[#F7F9FC] text-black flex items-center"
                onClick={() => {
                  window?.dataLayer &&
                    window.dataLayer.push({
                      event: "View External Listing",
                      Login: "pre",
                    });
                  const url = data?.originalSourceInfo?.source_vdp;
                  if (url) window.open(url, "_blank");
                }}
              >
                View listing on {data?.originalSourceInfo?.sourceSite}
                <span>
                  <img
                    className="pl-2"
                    src="/assets/arrow-link.svg"
                    alt="arrow-link"
                  ></img>
                </span>
              </button>

              <button
                className="btn-primary ml-3"
                onClick={() => openDealNowExplainedModal()}
              >
                DealNow Invite
              </button>
            </div>
          )}
        </div>

        {data?.originalSourceInfo && (
          <div className="flex items-center mt-3">
            <Image
              src={"/assets/fireIcon.svg"}
              width={15}
              height={17}
              alt="fire-icon"
            />
            <span className="ml-2 text-[12px] md:text-[14px]">
              Use DealNow to invite the seller to e-sign the bill of sale, send
              funds instantly, and safely close the sale on PrivateAuto.{" "}
            </span>
          </div>
        )}
        {isFullyVerified && !data?.originalSourceInfo && (
          <div className="flex items-center mt-3">
            <PaPaySetUpIcon />
            <span className="ml-2 text-[12px] md:text-[14px]">
              PrivateAuto Pay active - securely transfer money
            </span>
          </div>
        )}
      </section>
      <hr className="w-full hr-thick my-6" />
      <section className="w-full">
        <SpecsIcons listing={data}></SpecsIcons>

        <div className="mt-8">
          <h3 className="text-xl md:text-3xl font-bold opacity-90 mb-1">
            Description
          </h3>
          <div className="text-sm md:text-lg space-y-4 max-w-[820px]">
            <div
              className={`${
                !isExpanded ? "h-fit overflow-hidden" : ""
              } break-words`}
            >
              {(
                (isExpanded
                  ? data?.vehicleDescription
                  : fullDescription || ""
                ).split("\n") || []
              ).map((item: any, i: number) => (
                <p key={i}>{item}</p>
              ))}
            </div>
            {showUI && (
              <div className="flex mt-1">
                <button
                  onClick={toggleExpand}
                  className="text-blue-500 hover:underline underline"
                >
                  {isExpanded ? "Show less" : "Show more"}
                </button>
                <Image
                  src="/assets/chevron-right.svg"
                  alt="chevron"
                  className="pl-2"
                  width={17}
                  height={17}
                />
              </div>
            )}
          </div>
        </div>
      </section>
      <section className="w-full md:w-[820px] mt-6">
        <hr className="hr-thin" />
        <div className="mt-[8px] overflow-hidden">
          <div className="flex items-center" onClick={showAdditionalInfo}>
            <div className="w-[95%] font-bold text-base md:text-xl opacity-90 cursor-pointer">
              Additional Info
            </div>
            {isAdditionalInfo ? (
              <div className="font-semibold text-2xl md:text-3xl cursor-pointer">
                -
              </div>
            ) : (
              <div className="font-semibold text-2xl md:text-3xl cursor-pointer">
                +
              </div>
            )}
          </div>
          <AnimatePresence>
            {isAdditionalInfo && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: "fit-content" }}
                exit={{ height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mb-6">
                  <SpecsAdditionalDetails
                    listing={data}
                  ></SpecsAdditionalDetails>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <hr className="hr-thin mt-[8px]" />
        {!data?.originalSourceInfo && (
          <div className="mt-[8px] overflow-hidden">
            <div className="flex items-center" onClick={showSellerDisclosures}>
              <div className="w-[95%] font-bold text-base md:text-xl opacity-90 cursor-pointer">
                Seller Disclosures
              </div>
              {isSellerDisclosures ? (
                <div className="font-semibold text-2xl md:text-3xl cursor-pointer">
                  -
                </div>
              ) : (
                <div className="font-semibold text-2xl md:text-3xl cursor-pointer">
                  +
                </div>
              )}
            </div>
            <AnimatePresence>
              {isSellerDisclosures && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: "fit-content" }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div>
                    <div className="py-6 text-[16px]">
                      Are you aware of any damage or defects that would
                      materially effect the value of the vehicle?
                    </div>
                    {data.hasDamage ? (
                      <div className="flex space-x-3">
                        <div className="flex items-center cursor-pointer">
                          <Image
                            width={20}
                            height={20}
                            src="/assets/checkbox-checked.svg"
                            alt="checkbox-checked"
                          />
                          &nbsp;
                          <span>Yes</span>
                        </div>
                        <div className="flex items-center cursor-pointer">
                          <Image
                            width={20}
                            height={20}
                            src="/assets/checkbox-unchecked.svg"
                            alt="checkbox-unchecked"
                          />
                          &nbsp;
                          <span>No</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex space-x-3">
                        <div className="flex items-center cursor-pointer">
                          <Image
                            width={20}
                            height={20}
                            src="/assets/checkbox-unchecked.svg"
                            alt="checkbox-unchecked"
                          />
                          &nbsp;
                          <span>Yes</span>
                        </div>
                        <div className="flex items-center cursor-pointer">
                          <Image
                            width={20}
                            height={20}
                            src="/assets/checkbox-checked.svg"
                            alt="checkbox-checked"
                          />
                          &nbsp;
                          <span>No</span>
                        </div>
                      </div>
                    )}
                    {data.description && data.description != "" ? (
                      <div className="w-full bg-[#f7f9fc] text-sm font-normal px-4 py-4 my-4 border-[#dee2e6] border rounded">
                        {(data.description.split("\n") || []).map(
                          (item: any, i: number) => (
                            <p key={i}>{item}</p>
                          )
                        )}
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
        {!data?.originalSourceInfo && <hr className="hr-thin mt-[8px]" />}
      </section>

      <section className="relative">
        {data?.originalSourceInfo && (
          <div className=" top-0 left-0 w-full md:w-[820px] h-full flex flex-col justify-start bg-[#FFEDDE] rounded-lg my-8 ">
            <div className="flex items-center p-6">
              <Image
                src={"/assets/clipboard-light.svg"}
                width={24}
                height={30}
                className="w-[37px] h-[30px] pr-4"
                alt={""}
              />
              <div className="font-[700] text-[16px]">
                Purchase vehicle history + market report for this vehicle
                <div className="font-[400] text-[14px]  pt-1">
                  Find out what this car is worth and if it’s been in any
                  accidents before doing the DealNow
                </div>
              </div>
            </div>

            <button
              className="btn-outline-primary  w-[200px] ml-[60px] mb-6"
              onClick={() => {
                window?.dataLayer &&
                  window.dataLayer.push({
                    event: "Purchase Reports",
                    Login: "pre",
                  });
                setRedirectUrl(`/listing/${data.slug}?puchaseReports=true`);
                setIsSignup(true, {
                  redirectUrl: `/listing/${data.slug}?puchaseReports=true`,
                });
              }}
            >
              Purchase reports
            </button>
          </div>
        )}
        {!data?.originalSourceInfo && (
          <>
            {data?.status !== "sold" && (
              <>
                <div
                  className="w-full md:w-[820px] bg-white border rounded-lg border-[#D3D3D3] flex justify-between items-center px-3 xs:px-6 py-4 my-6 cursor-pointer"
                  onClick={() => historyReport()}
                >
                  <div className="flex items-center space-x-2 xs:space-x-4 cursor-pointer">
                    <NoteIcon />
                    <p className="text-base md:text-lg text-[#2b2f33] font-bold">
                      Vehicle history report
                    </p>
                  </div>
                  <button className="btn-outline-primary">View</button>
                </div>
                <PricepointPanelSection listing={data} />
              </>
            )}
          </>
        )}
      </section>

      {data?.originalSourceInfo && (
        <>
          <div className="w-full h-auto flex py-5 flex-col sm:flex-row justify-center items-center mb-5 bg-[#eff0f6]">
            <div className="max-w-[442px] mx-4 sm:mx-0">
              <div className="text-[34px] font-bold max-w-[420px] sm:text-[24px]">
                How DealNow works for off-site listings:
              </div>
              <div className="mt-3 text-[18px] font-normal sm:text-[16px]">
                DealNow is a quick way to do a private-party car transaction.
                It’s ideal for when you want a safe and convenient way to close
                the deal.
              </div>
              <div className="flex items-center mt-4 text-[16px] font-semibold">
                <img
                  src="/assets/fireIcon.svg"
                  alt="fireIcon"
                  className="mr-3 w-[30px]"
                />
                <div className="max-w-[265px]">
                  Use DealNow to send an offer to a seller anywhere.
                </div>
              </div>
              <div className="flex items-center mt-4 text-[16px] font-semibold">
                <img
                  src="/assets/externalLinkIcon.svg"
                  alt="fireIcon"
                  className="mr-3 w-[30px]"
                />
                <div className="max-w-[265px]">
                  Message the seller the DealNow invite on the external listing
                  site
                </div>
              </div>
              <div className="flex items-center mt-4 text-[16px] font-semibold">
                <img
                  src="/assets/smallPALogo.svg"
                  alt="fireIcon"
                  className="mr-3 w-[30px]"
                />
                <div className="max-w-[265px]">
                  Close a deal no matter where the car is listed or located.
                </div>
              </div>
              <div className="flex items-center mt-4 text-[16px] font-semibold">
                <img
                  src="/assets/dollar-circle-green.svg"
                  alt="fireIcon"
                  className="mr-3 w-[30px]"
                />
                <div className="max-w-[265px]">
                  Instant transfer of money P2P with no transaction fees
                </div>
              </div>
            </div>
            <img
              src="/assets/dealNowExplained.svg"
              className="w-[404px] h-[507px] sm:w-[334px] sm:h-[437px] ml-4"
              alt="explaned-DealNow"
            />
          </div>
        </>
      )}

      {/* <DealNowExplainedModal
        isDealNowExplainedModal={isDealNowExplainedModal}
        setIsDealNowExplainedModal={setIsDealNowExplainedModal}
        slug={data.slug}
      /> */}
    </div>
  );
};

export default VehicleDetailSection;
